import { Swiper as ReactSwiper, SwiperSlide } from 'swiper/react';
import { Controller } from 'swiper/modules';
import 'swiper/css';
import { FormattedMessage } from 'react-intl';
import { useState } from 'react';
import Swiper from 'swiper';
import Button from '@/components/Button';
import LocaleSelection from '@/components/LocaleSelection/LocaleSelection';
import { useWebViewRouter } from '@/hooks/useWebViewRouter';
import SkipButton from '../_components/SkipButton';
import { Image } from '@/components/Image';
import { trackEvent } from '@/components/Event/_lib/eventTrack';
const ONBOARDING_COPY: Record<string, string> = {
  onboarding1_description1: 'First, choose a group and find the photocard you want.',
  onboarding1_description2: 'You can find it more quickly by using the search or filters.',
  onboarding2_description1: 'Second! Select the photocard you want,',
  onboarding2_description2: 'click [Add to cart].',
  onboarding3_description1: 'Third! You can view your cart on the lower right-hand side of your screen.',
  onboarding3_description2: "When you're ready to checkout click [Place an order].",
  onboarding4_description1: 'Fourth! Fill in and review your information.',
  onboarding4_description2: 'After checking, click [Proceed to checkout].',
  onboarding5_description1: "Lastly! Once you've placed your order, please pay at the counter.",
  onboarding5_description2: 'You are now the proud owner of these special photocards!'
};

const PAGES = [
  {
    imageSrc: '/onboarding/screen1.webp',
    description1Id: 'onboarding1_description1',
    description2Id: 'onboarding1_description2'
  },
  {
    imageSrc: '/onboarding/screen2.webp',
    description1Id: 'onboarding2_description1',
    description2Id: 'onboarding2_description2'
  },
  {
    imageSrc: '/onboarding/screen3.webp',
    description1Id: 'onboarding3_description1',
    description2Id: 'onboarding3_description2'
  },
  {
    imageSrc: '/onboarding/screen4.webp',
    description1Id: 'onboarding4_description1',
    description2Id: 'onboarding4_description2'
  },
  {
    imageSrc: '/onboarding/screen5.webp',
    imageClassName: 'onboarding-slide-image-last',
    description1Id: 'onboarding5_description1',
    description2Id: 'onboarding5_description2'
  }
] as const;

interface PaginationProps {
  total: number;
  current: number;
}

const Pagination = ({ total, current }: PaginationProps) => (
  <div className="flex flex-row gap-4 mb-10">
    {new Array(total).fill(0).map((v, index) => (
      <div key={index} className={`w-[11px] h-[11px] rounded-[12px] ${index === current ? 'bg-[#ff6060]' : 'bg-[#dadada]'}`} />
    ))}
  </div>
);

export const OnboardingPage = () => {
  const [page, setPage] = useState(0);
  const [controlledSwiper, setControlledSwiper] = useState<Swiper | null>(null);
  const { direct } = useWebViewRouter();

  const goToNextPage = () => {
    controlledSwiper?.slideNext();
  };

  const goToPrevPage = () => {
    controlledSwiper?.slidePrev();
  };

  return (
    <div className="w-screen h-screen flex flex-row items-center justify-between px-[60px]">
      <button
        className="flex flex-col gap-1 items-center justify-center bg-[#f5f5f5] rounded-[20px] w-[100px] h-[100px] cursor-pointer"
        onClick={page === 0 ? () => direct('/') : goToPrevPage}
      >
        <Image src="/icons/arrow-right.svg" alt="arrow icon" className="rotate-180" width={40} />
        <span className="text-[12px] leading-4 font-bold">
          <FormattedMessage id="onboarding_prev" defaultMessage="Prev" />
        </span>
      </button>
      <div className="flex-1 flex flex-col max-w-[924px] items-center justify-center mt-[70px]">
        <Pagination total={PAGES.length + 1} current={page} />
        <ReactSwiper
          className="w-full h-full"
          modules={[Controller]}
          controller={{ control: controlledSwiper }}
          onSwiper={(swiper) => {
            setControlledSwiper(swiper);
          }}
          slidesPerView={1}
          onSlideChange={(swiper) => {
            setPage(swiper.activeIndex);
          }}
        >
          {PAGES.map((pageInfo, idx) => (
            <SwiperSlide key={pageInfo.imageSrc}>
              <div className="flex flex-col items-center justify-center gap-[34px]">
                <Image src={pageInfo.imageSrc} alt="screenshot" width={900} />
                <span className="text-[20px] leading-7 font-medium text-center block">
                  <FormattedMessage
                    id={pageInfo.description1Id}
                    defaultMessage={ONBOARDING_COPY[pageInfo.description1Id] ?? ''}
                  />
                  <br />
                  <FormattedMessage
                    id={pageInfo.description2Id}
                    defaultMessage={ONBOARDING_COPY[pageInfo.description2Id] ?? ''}
                  />
                </span>
                <div
                  onClick={() =>
                    trackEvent('Onboarding_Click_Skip', {
                      Page: idx + 1
                    })
                  }
                >
                  <SkipButton />
                </div>
              </div>
            </SwiperSlide>
          ))}
          <SwiperSlide>
            <div className="flex flex-col items-center justify-center gap-[34px]">
              <Image src="/images/bi.png" alt="logo" className="w-[216px]" width={216} />
              <Image src="/onboarding/screen6.png" alt="character" width={300} />
              <span className="text-[20px] leading-7 font-medium text-center block">
                <FormattedMessage
                  id="onboarding6_description1"
                  defaultMessage="You'll be notified by email when your order is ready for pick-up in store!"
                />
                <br />
                <FormattedMessage id="onboarding6_description2" defaultMessage="Please pick it up on time!" />
              </span>
              <Button
                data-primary
                onClick={() => {
                  direct('/photocard');
                  trackEvent('Onboarding_Click_Start', {
                    Page: 'final'
                  });
                }}
              >
                <FormattedMessage id="onboarding_button" defaultMessage="Start" />
              </Button>
            </div>
          </SwiperSlide>
        </ReactSwiper>
      </div>
      {page !== 5 ? (
        <button
          className="flex flex-col gap-1 items-center justify-center bg-[#f2f2f2] rounded-[20px] w-[100px] h-[100px] cursor-pointer"
          onClick={goToNextPage}
        >
          <Image src="/icons/arrow-right.svg" alt="arrow icon" className="w-10 h-10" width={40} height={40} />
          <span className="text-[12px] leading-4 font-bold">
            <FormattedMessage id="onboarding_next" defaultMessage="Next" />
          </span>
        </button>
      ) : (
        <div className="w-[100px]" />
      )}
      <LocaleSelection />
    </div>
  );
};
